/**
 * @file seoRelease.js
 * @description The one rule for WHEN a public SEO page goes live (owner 2026-10-20: city and state pages are
 *   released one batch a week, not all at once). A route in seoRoutes.json may carry `publishOn: "YYYY-MM-DD"`;
 *   it is live once the release date has reached it. No `publishOn` = live (every page that existed before the
 *   engine).
 *
 *   The release date is the BUILD date: CI sets REACT_APP_SEO_RELEASE_DATE (America/New_York) before
 *   `npm run build`, so the app bundle, the sitemap, the prerender and _routes.json all agree, and a page appears
 *   only after the build that releases it (a weekly scheduled build in .github/workflows/deploy.yml, plus any push).
 *   Without the variable (local dev, tests) the date is today in New York.
 *
 *   CommonJS on purpose: the React app (via webpack) and the Node build scripts require the same file.
 * @module data/seoRelease
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Pages held back from release (owner 2026-10-23, with David): a scheduled page that would ship under the 40%
 * cross-city uniqueness bar stays unpublished, even past its publishOn date, until it is fixed. The weekly lookahead
 * (scripts/seo-lookahead.js, .github/workflows/seo-lookahead.yml) writes this file; nothing else should.
 */
const SEO_HOLDS = require("./seoHolds.json").pages || {};

/** Today in America/New_York as YYYY-MM-DD. */
function todayInNewYork(now = new Date()) {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/**
 * The build's pinned release date, read as literal `process.env.X` expressions: in the browser bundle webpack only
 * replaces those exact expressions (`typeof process` is "undefined" there), so reading `process.env` as an object
 * would silently fall back to today and never release a scheduled page in the app.
 */
function buildEnv() {
  try {
    return {
      SEO_RELEASE_DATE: process.env.SEO_RELEASE_DATE,
      REACT_APP_SEO_RELEASE_DATE: process.env.REACT_APP_SEO_RELEASE_DATE,
      // The lookahead build sets this to measure held pages too; real builds never do.
      SEO_IGNORE_HOLDS: process.env.SEO_IGNORE_HOLDS,
      REACT_APP_SEO_IGNORE_HOLDS: process.env.REACT_APP_SEO_IGNORE_HOLDS,
    };
  } catch {
    return {};
  }
}

/**
 * The date pages are released up to (inclusive).
 * @param {Object} [env] defaults to the build's environment (buildEnv)
 * @param {Date} [now]
 */
function seoReleaseDate(env, now) {
  const e = env || buildEnv();
  const pinned = e.SEO_RELEASE_DATE || e.REACT_APP_SEO_RELEASE_DATE;
  if (pinned && DATE_RE.test(pinned)) return pinned;
  return todayInNewYork(now);
}

/** True when the route is held back (seoHolds.json), unless this build ignores holds. */
function isRouteHeld(route, env) {
  if (!route || !route.path) return false;
  const e = env || buildEnv();
  if (e.SEO_IGNORE_HOLDS === "true" || e.REACT_APP_SEO_IGNORE_HOLDS === "true") return false;
  return Object.prototype.hasOwnProperty.call(SEO_HOLDS, route.path.replace(/^\//, ""));
}

/**
 * True when a route is live on `releaseDate`: no publishOn = always live; a scheduled route once its date has
 * arrived, unless it is held back (isRouteHeld). Only scheduled pages can be held: a page that is already live is
 * never pulled by this file.
 */
function isRouteLive(route, releaseDate) {
  if (!route || !route.publishOn) return true;
  return route.publishOn <= (releaseDate || seoReleaseDate()) && !isRouteHeld(route);
}

/** The routes live on `releaseDate`. */
function liveRoutes(routes, releaseDate) {
  const d = releaseDate || seoReleaseDate();
  return (routes || []).filter((r) => isRouteLive(r, d));
}

/**
 * The release days (owner 2026-10-21: four a week, Monday to Thursday, so pages get found sooner), as UTC weekday
 * numbers (0 = Sunday). Mirrored by the scheduled build's cron in .github/workflows/deploy.yml ("0 11 * * 1-4"):
 * change both together. One batch (one state) per release day.
 */
const RELEASE_WEEKDAYS = [1, 2, 3, 4];

/** True when `ymd` falls on a release day. */
function isReleaseDay(ymd) {
  if (!DATE_RE.test(ymd || "")) return false;
  return RELEASE_WEEKDAYS.includes(new Date(`${ymd}T12:00:00Z`).getUTCDay());
}

/** The first `n` release days on or after `fromYmd` (YYYY-MM-DD). */
function releaseDaysFrom(fromYmd, n) {
  const out = [];
  const t = new Date(`${fromYmd}T12:00:00Z`);
  while (out.length < n) {
    const ymd = t.toISOString().slice(0, 10);
    if (isReleaseDay(ymd)) out.push(ymd);
    t.setUTCDate(t.getUTCDate() + 1);
  }
  return out;
}

module.exports = { DATE_RE, SEO_HOLDS, todayInNewYork, seoReleaseDate, isRouteHeld, isRouteLive, liveRoutes, RELEASE_WEEKDAYS, isReleaseDay, releaseDaysFrom };
